import { read, TABLE } from '../data/db';
import { ourPlayers } from '../data/repos';
import type { AttributeKey, AttributeRating, OurPlayerRow, Position } from '../types';
import { dispatch } from './bus';
import type { DispatchResult } from './bus';
import { newId } from './ids';

/**
 * B spec §4.5 — **the My Team roster facade** (build unit B-2, scenario 2).
 *
 * The A convention, unchanged: `load*` is one readonly transaction; each write dispatches
 * exactly one command and returns `DispatchResult`; `onCommitted` is not offered because
 * nothing here is a capture ack — the My Team screen re-reads after a successful write, the
 * imperative-re-read convention every A screen follows.
 *
 * ── The scope is one table, and that is a mechanism ──────────────────────────
 *
 * The roster references no other row and nothing references it (B-1: profile-scoped, no
 * cascade). `matches`, `players` and `teams` are absent from every scope below, so "My Team is
 * not the opponent file" is a runtime property — a read of an opponent row from here throws.
 */

/** What the My Team sheet edits. `id` absent = create; the facade mints one. */
export interface RosterPlayerInput {
  readonly id?: string;
  /** §3.7.36 — required, 1–99. Refused with the existing `ERR_SHIRT_NUMBER_RANGE`. */
  readonly jerseyNumber: number;
  /** §3.7.36 — required, non-empty. Ruling R-1 keeps it here, in the archive and on the wire. */
  readonly firstName: string;
  readonly primaryPosition: Position;
  /**
   * §3.7.36's **execution filter**. `[]` is a meaningful final state — "must not be moved at all"
   * — and never a not-yet-filled-in blank, which is why the sheet styles it as a statement.
   */
  readonly secondaryPositions: readonly Position[];
  /** §3.7.37 — sparse. An absent key means NOT ASSESSED; there is no `null` rating. */
  readonly ratings: Readonly<Partial<Record<AttributeKey, AttributeRating>>>;
  readonly active: boolean;
}

/** The whole roster, in the stable jersey-ascending order the My Team list renders. */
export function loadRoster(): Promise<OurPlayerRow[]> {
  return read([TABLE.ourPlayers], () => ourPlayers.listOurPlayers(), 'loadRoster');
}

/**
 * B §4.2 — create or edit one player. One command either way (`UpsertOurPlayer`); the insert-vs-
 * edit decision belongs to the handler's in-transaction read, not to whether the sheet believed
 * it was editing. On the edit arm the handler keeps the stored `createdAt`, so the `at` stamped
 * here only ever dates a genuinely new row.
 */
export function upsertPlayer(
  input: RosterPlayerInput,
  at: number = Date.now(),
): Promise<DispatchResult> {
  const row: OurPlayerRow = {
    id: input.id ?? newId(),
    jerseyNumber: input.jerseyNumber,
    firstName: input.firstName.trim(),
    primaryPosition: input.primaryPosition,
    // Copied out of the readonly input, and deduplicated with the primary removed, so the row that
    // reaches the handler is the one the user meant. The handler still refuses a violating list
    // (`ERR_ROSTER_POSITION_INVALID`) — this normalises the sheet's output, it does not replace the
    // rule, because the facade is not the only caller a stale screen can reach.
    secondaryPositions: [...new Set(input.secondaryPositions)].filter(
      (position) => position !== input.primaryPosition,
    ),
    ratings: { ...input.ratings },
    active: input.active,
    createdAt: at,
    updatedAt: at,
  };
  return dispatch({ type: 'UpsertOurPlayer', row });
}

/** B §4.2 — remove one player. A stale double-delete returns `ERR_ROSTER_NOT_FOUND` as a value. */
export function deletePlayer(playerId: string): Promise<DispatchResult> {
  return dispatch({ type: 'DeleteOurPlayer', playerId });
}
